import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';

interface GameInstance {
    id: string;
    name: string;
    status: string;
    campaign: {
        id: string;
        name: string;
        slug: string;
    };
    game_template: {
        id: string;
        name: string;
        type: string;
        code: string;
        version: string;
    };
    config: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export default function GameManager() {
    const [games, setGames] = useState<GameInstance[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCampaign, setFilterCampaign] = useState('');
    const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
    const [showAddGameModal, setShowAddGameModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [gameName, setGameName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [templates, setTemplates] = useState<any[]>([]);
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [instances, campaignList, templateList] = await Promise.all([
                api.getGameInstances(1, 100),
                api.getCampaigns(tenantId, 1, 100),
                api.getGameTemplates(1, 100),
            ]);

            const { items: gameItems } = parsePaginatedResponse(instances);
            const { items: campaignItems } = parsePaginatedResponse(campaignList);
            const { items: templateItems } = parsePaginatedResponse(templateList);
            setGames(gameItems);
            setCampaigns(campaignItems);
            setTemplates(templateItems);
        } catch (err) {
            console.error('Failed to fetch game data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddGame = async () => {
        if (!selectedTemplate || !selectedCampaign || !gameName) return;
        try {
            await api.createGameInstanceFromTemplate(selectedTemplate, selectedCampaign, {
                tenantId,
                name: gameName,
                config: {},
            });
            await fetchData();
            setShowAddGameModal(false);
            setSelectedTemplate('');
            setSelectedCampaign('');
            setGameName('');
        } catch (err) {
            console.error('Failed to add game:', err);
        }
    };

    const handleUploadGame = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        setUploading(true);
        try {
            const campaignId = String(formData.get('campaignId') || '');
            if (!campaignId) {
                alert('Please select a campaign.');
                return;
            }
            formData.delete('campaignId');
            const result = await api.uploadGameZip(campaignId, formData);
            if (result.success) {
                await fetchData();
                setShowUploadModal(false);
            } else {
                alert(result.message || 'Upload failed');
            }
        } catch (err) {
            console.error('Failed to upload game:', err);
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteGame = async (gameId: string) => {
        if (!confirm('Are you sure you want to delete this game instance?')) return;
        try {
            await api.deleteGameInstance(gameId);
            setGames(prev => prev.filter(g => g.id !== gameId));
        } catch (err) {
            console.error('Failed to delete game:', err);
        }
    };

    const handleToggleGameStatus = async (gameId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            await api.updateGameInstanceStatus(gameId, newStatus);
            setGames(prev =>
                prev.map(g => g.id === gameId ? { ...g, status: newStatus } : g)
            );
        } catch (err) {
            console.error('Failed to update game status:', err);
        }
    };

    const gameTypeIcons: Record<string, string> = {
        'lucky-wheel': '🎡',
        'flip-card': '🃏',
        'scratch-card': '🎫',
        'egg-pick': '🥚',
        'lucky_wheel': '🎡',
        'flip_card': '🃏',
        'scratch_card': '🎫',
        'egg_pick': '🥚',
        'random_code': '🎫',
        'default': '🎮',
    };

    const getGameTypeCode = (template: any) => template?.game_type?.code || template?.type || template?.code?.replace(/_v\d+$/, '');

    const filteredGames = games.filter(game => {
        if (filterStatus && game.status !== filterStatus) return false;
        if (filterCampaign && game.campaign?.id !== filterCampaign) return false;
        return true;
    });

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Game Manager</h1>
                    <p className="text-gray-500 mt-1">Manage all game instances across campaigns</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                        <span>📦</span> Upload ZIP
                    </button>
                    <button
                        onClick={() => setShowAddGameModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <span>➕</span> Add Game
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm text-gray-500 mb-1">Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-500 mb-1">Campaign</label>
                        <select
                            value={filterCampaign}
                            onChange={(e) => setFilterCampaign(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">All Campaigns</option>
                            {campaigns.map(campaign => (
                                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Games Grid */}
            {filteredGames.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="text-6xl mb-4">🎮</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No games found</h3>
                    <p className="text-gray-500 mb-4">Get started by adding a game or uploading a ZIP file</p>
                    <div className="flex justify-center gap-2">
                        <button
                            onClick={() => setShowAddGameModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                            Add from Template
                        </button>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                        >
                            Upload ZIP
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredGames.map((game) => (
                        <div key={game.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-4xl">
                                            {gameTypeIcons[getGameTypeCode(game.game_template)] || gameTypeIcons.default}
                                        </span>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{game.name}</h3>
                                            <p className="text-sm text-gray-500">
                                                {game.game_template?.name || getGameTypeCode(game.game_template)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleToggleGameStatus(game.id, game.status)}
                                        className={`px-2 py-1 text-xs font-medium rounded-full ${game.status === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}
                                    >
                                        {game.status}
                                    </button>
                                </div>

                                <div className="space-y-2 text-sm text-gray-500">
                                    {game.campaign && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400">📣</span>
                                            <span className="truncate">{game.campaign.name}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-400">📅</span>
                                        <span>{new Date(game.created_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={() => handleDeleteGame(game.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Game Modal */}
            {showAddGameModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Game from Template</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                                <select
                                    value={selectedCampaign}
                                    onChange={(e) => setSelectedCampaign(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select a campaign...</option>
                                    {campaigns.map((campaign) => (
                                        <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Game Name</label>
                                <input
                                    type="text"
                                    value={gameName}
                                    onChange={(e) => setGameName(e.target.value)}
                                    placeholder="My Lucky Wheel"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                                <select
                                    value={selectedTemplate}
                                    onChange={(e) => setSelectedTemplate(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select a template...</option>
                                    {templates.map((template) => (
                                        <option key={template.id} value={template.id}>
                                            {gameTypeIcons[getGameTypeCode(template)] || '🎮'} {template.name || template.code} ({getGameTypeCode(template) || template.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowAddGameModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddGame}
                                disabled={!selectedTemplate || !selectedCampaign || !gameName}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                Add Game
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload ZIP Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Game ZIP</h3>
                        <form onSubmit={handleUploadGame}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                                    <select
                                        name="campaignId"
                                        defaultValue=""
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    >
                                        <option value="">Select a campaign...</option>
                                        {campaigns.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Game ZIP File</label>
                                    <input
                                        type="file"
                                        name="gameZip"
                                        accept=".zip"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Upload a ZIP file containing template.json or package.json
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowUploadModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
